import React, { useEffect } from "react";
import {
  Checkbox,
  CompoundButton,
  Field,
  Text,
} from "@fluentui/react-components";
import { Board24Filled, Board24Regular } from "@fluentui/react-icons/svg/board";
import {
  Status24Filled,
  Status24Regular,
} from "@fluentui/react-icons/svg/status";
import { useParams } from "react-router-dom";
import {
  type ConnectionEditState,
  type ConnectionEditStateUpdater,
  type Status,
  type SourceStatusMapping,
} from "@onetasklist/shared";
import {
  useVstsSourceSettingsText,
  useVstsSourceSettingsViewModel,
} from "@onetasklist/shared/sources/vsts";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { useToast } from "@contexts/ToastContext";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SourceMultiTagPicker } from "./components/SourceMultiTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";
import { StatusMappingSection } from "./components/StatusMappingSection";
import { VstsStatusMappingCard } from "./components/VstsStatusMappingCard";

interface VstsSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
  boardStatuses: Status[];
}

export const VstsSourceSettings: React.FC<VstsSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
  boardStatuses,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useVstsSourceSettingsText(t);
  const vm = useVstsSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });
  const toast = useToast();

  useEffect(() => {
    if (vm.hasError) {
      toast.error(text.loadErrorText);
    }
  }, [vm.hasError, text.loadErrorText, toast]);

  if (!vm.isVisible) {
    return null;
  }

  const availableWorkItemTypesToAdd = vm.statusWorkItemTypes.filter(
    (wt) => !vm.workItemTypeMappings.some((m) => m.workItemType.url === wt.url),
  );

  // Board mode and status mode each show one card in this slot, so they share
  // an offset. Column mapping only renders in board mode when columns are
  // selected, so display offset follows sequentially after whatever is shown.
  const scopeCardOffset = sectionOffset;
  const modeConfigCardOffset = sectionOffset + 1;
  const importOrMappingCardOffset = sectionOffset + 2;
  const columnMappingCardOffset = sectionOffset + 3;
  const showColumnMappingSection =
    vm.isBoardMode && vm.selectedColumnsToLoad.length > 0;
  const displayCardOffset = showColumnMappingSection
    ? sectionOffset + 4
    : sectionOffset + 3;

  return (
    <>
      {/* Card ②: Source Scope */}
      <SettingsSectionCard
        sectionOffset={scopeCardOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.vsts.sectionSource",
        )}
      >
        <Field
          orientation="horizontal"
          label={text.accountLabelText}
          hint={text.accountHintText}
        >
          <SourceSingleTagPicker
            selectedId={
              vm.selectedAccountName === vm.noneOptionValue
                ? undefined
                : vm.selectedAccountName
            }
            selectedLabel={
              vm.selectedAccountName === vm.noneOptionValue
                ? undefined
                : vm.selectedAccountName
            }
            query={vm.accountQuery}
            placeholder={text.accountPlaceholderText}
            options={vm.accounts.map((account) => ({
              id: account.accountName,
              label: account.accountName,
            }))}
            onQueryChange={vm.setAccountQuery}
            onSelect={vm.onSelectAccount}
            clearSelectionText={text.accountPlaceholderText}
            isLoading={vm.isLoadingAccounts}
          />
        </Field>

        <Field orientation="horizontal" label={text.modeLabelText}>
          <div style={{ display: "flex", gap: "12px" }}>
            <CompoundButton
              appearance={vm.isBoardMode ? "secondary" : "subtle"}
              icon={vm.isBoardMode ? <Board24Filled /> : <Board24Regular />}
              secondaryContent={text.boardModeHint}
              onClick={() => vm.onSelectMode("BoardMode")}
              style={{ flex: 1, justifyContent: "flex-start" }}
            >
              {text.boardModeLabel}
            </CompoundButton>
            <CompoundButton
              appearance={vm.isStatusMode ? "secondary" : "subtle"}
              icon={vm.isStatusMode ? <Status24Filled /> : <Status24Regular />}
              secondaryContent={text.statusModeHint}
              onClick={() => vm.onSelectMode("StatusMode")}
              style={{ flex: 1, justifyContent: "flex-start" }}
            >
              {text.statusModeLabel}
            </CompoundButton>
          </div>
        </Field>
      </SettingsSectionCard>

      {/* Card ③: Status Mode Projects (status mode only) */}
      {vm.isStatusMode && (
        <SettingsSectionCard
          sectionOffset={modeConfigCardOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.vsts.sectionProjects",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.statusModeProjectsLabelText}
            hint={text.statusModeProjectsHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.statusModeProjects.map((p) => p.id)}
              query={vm.statusModeProjectsQuery}
              placeholder={text.statusModeProjectsPlaceholderText}
              options={vm.projects.map((project) => ({
                id: project.id,
                label: project.name,
              }))}
              onQueryChange={vm.setStatusModeProjectsQuery}
              onSelectionChange={vm.onSelectStatusModeProjects}
              minQueryLength={0}
              typeToSearchText={text.statusModeProjectsHintText}
              disabled={vm.selectedAccountName === vm.noneOptionValue}
              isLoading={vm.isLoadingProjects}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {/* Card ③: Project, Team & Board (board mode only) */}
      {vm.isBoardMode && (
        <SettingsSectionCard
          sectionOffset={modeConfigCardOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.vsts.sectionTeamBoard",
          )}
        >
          {vm.selectedAccountName !== vm.noneOptionValue && (
            <Field
              orientation="horizontal"
              label={text.projectLabelText}
              hint={text.projectHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedProjectId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedProjectId
                }
                selectedLabel={
                  vm.projects.find(
                    (project) => project.id === vm.selectedProjectId,
                  )?.name
                }
                query={vm.projectQuery}
                placeholder={text.projectPlaceholderText}
                options={vm.projects.map((project) => ({
                  id: project.id,
                  label: project.name,
                }))}
                onQueryChange={vm.setProjectQuery}
                onSelect={vm.onSelectProject}
                clearSelectionText={text.projectPlaceholderText}
                isLoading={vm.isLoadingProjects}
              />
            </Field>
          )}

          {vm.selectedProjectId !== vm.noneOptionValue && (
            <Field
              orientation="horizontal"
              label={text.teamLabelText}
              hint={text.teamHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedTeamId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedTeamId
                }
                selectedLabel={
                  vm.teams.find((team) => team.id === vm.selectedTeamId)?.name
                }
                query={vm.teamQuery}
                placeholder={text.teamPlaceholderText}
                options={vm.teams.map((team) => ({
                  id: team.id,
                  label: team.name,
                }))}
                onQueryChange={vm.setTeamQuery}
                onSelect={vm.onSelectTeam}
                clearSelectionText={text.teamPlaceholderText}
                isLoading={vm.isLoadingTeams}
              />
            </Field>
          )}

          {vm.selectedTeamId !== vm.noneOptionValue && (
            <Field
              orientation="horizontal"
              label={text.boardLabelText}
              hint={text.boardHintText}
            >
              <SourceSingleTagPicker
                selectedId={
                  vm.selectedBoardId === vm.noneOptionValue
                    ? undefined
                    : vm.selectedBoardId
                }
                selectedLabel={
                  vm.boards.find((board) => board.id === vm.selectedBoardId)
                    ?.name
                }
                query={vm.boardQuery}
                placeholder={text.boardPlaceholderText}
                options={vm.boards.map((board) => ({
                  id: board.id,
                  label: board.name,
                }))}
                onQueryChange={vm.setBoardQuery}
                onSelect={vm.onSelectBoard}
                clearSelectionText={text.boardPlaceholderText}
                isLoading={vm.isLoadingBoards}
              />
            </Field>
          )}
        </SettingsSectionCard>
      )}

      {/* Card ④: What to Import (board mode) or Status Mapping (status mode) */}
      {vm.isBoardMode && vm.columns.length > 0 && (
        <SettingsSectionCard
          sectionOffset={importOrMappingCardOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.vsts.sectionImport",
          )}
        >
          <Field
            orientation="horizontal"
            label={text.workItemTypeLabelText}
            hint={text.workItemTypeHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedWorkItemTypeFilter}
              query={vm.workItemTypeFilterQuery}
              placeholder={text.workItemTypePlaceholderText}
              options={vm.workItemTypeNames.map((n) => ({ id: n, label: n }))}
              onQueryChange={vm.setWorkItemTypeFilterQuery}
              onSelectionChange={vm.onSelectWorkItemTypeFilter}
              minQueryLength={0}
              typeToSearchText={text.workItemTypeHintText}
              isLoading={vm.isLoadingBoards}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.columnsToLoadLabelText}
            hint={text.columnsToLoadHintText}
          >
            <SourceMultiTagPicker
              selectedIds={vm.selectedColumnsToLoad}
              query={vm.columnsToLoadQuery}
              placeholder={text.columnsToLoadPlaceholderText}
              options={vm.columnOptions}
              onQueryChange={vm.setColumnsToLoadQuery}
              onSelectionChange={vm.onSelectColumnsToLoad}
              minQueryLength={0}
              typeToSearchText={text.columnsToLoadHintText}
              isLoading={vm.isLoadingBoards}
            />
          </Field>

          <Field
            orientation="horizontal"
            label={text.doneColumnLabelText}
            hint={text.doneColumnHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedDoneColumnKey === vm.noneOptionValue
                  ? undefined
                  : vm.selectedDoneColumnKey
              }
              selectedLabel={vm.selectedDoneColumnLabel}
              query={vm.doneColumnQuery}
              placeholder={text.doneColumnPlaceholderText}
              options={vm.columnOptions}
              onQueryChange={vm.setDoneColumnQuery}
              onSelect={vm.onSelectDoneColumn}
              clearSelectionText={text.doneColumnPlaceholderText}
              isLoading={vm.isLoadingBoards}
            />
          </Field>
        </SettingsSectionCard>
      )}

      {vm.isBoardMode && vm.selectedColumnsToLoad.length > 0 && (
        <StatusMappingSection
          sectionNumber={columnMappingCardOffset}
          items={vm.statusMappingColumns.map((c) => ({
            id: c.id,
            name: c.label,
          }))}
          mappings={
            ((
              (editingConnection.configuration as Record<string, unknown>)
                ?.boardMode as Record<string, unknown> | undefined
            )?.statusMappings as SourceStatusMapping[] | undefined) ?? []
          }
          boardStatuses={boardStatuses}
          onChange={vm.onSetBoardModeStatusMappings}
        />
      )}

      {vm.isStatusMode && (
        <VstsStatusMappingCard
          sectionOffset={importOrMappingCardOffset}
          title={t(
            "board.connections.catalogue.sourceSettings.vsts.sectionMapping",
          )}
          mappings={vm.workItemTypeMappings}
          availableToAdd={availableWorkItemTypesToAdd}
          isLoading={vm.isLoadingStatusWorkItemTypes}
          addLabelText={text.addWorkItemTypeLabelText}
          addPlaceholderText={text.addWorkItemTypePlaceholderText}
          statusesToLoadPlaceholderText={text.statusesToLoadPlaceholderText}
          doneStatusPlaceholderText={text.doneStatusPlaceholderText}
          loadingText={text.loadingText}
          boardStatuses={boardStatuses}
          onAdd={vm.onAddWorkItemTypeMapping}
          onRemove={vm.onRemoveWorkItemTypeMapping}
          onSetStatusesToLoad={vm.onSetMappingStatusesToLoad}
          onSetDoneStatus={vm.onSetMappingDoneStatus}
          onSetStatusMappings={vm.onSetMappingStatusMappings}
        />
      )}

      {/* Card ⑥: Display Options */}
      <SettingsSectionCard
        sectionOffset={displayCardOffset}
        title={t(
          "board.connections.catalogue.sourceSettings.vsts.sectionDisplay",
        )}
      >
        {vm.identityFields.length > 0 && (
          <Field
            orientation="horizontal"
            label={text.assignedToFieldLabelText}
            hint={text.assignedToFieldHintText}
          >
            <SourceSingleTagPicker
              selectedId={
                vm.selectedIdentityFieldReferenceName === vm.noneOptionValue
                  ? undefined
                  : vm.selectedIdentityFieldReferenceName
              }
              selectedLabel={
                vm.identityFields.find(
                  (f) =>
                    f.referenceName === vm.selectedIdentityFieldReferenceName,
                )?.name
              }
              query={vm.identityFieldQuery}
              placeholder={text.assignedToFieldPlaceholderText}
              options={vm.identityFields.map((f) => ({
                id: f.referenceName,
                label: f.name ?? f.referenceName,
              }))}
              onQueryChange={vm.setIdentityFieldQuery}
              onSelect={vm.onSelectIdentityField}
              clearSelectionText={text.assignedToFieldPlaceholderText}
              isLoading={vm.isLoadingIdentityFields}
            />
          </Field>
        )}

        <Field
          orientation="horizontal"
          label={text.whenDoneAssignToCreatorLabelText}
          hint={text.whenDoneAssignToCreatorHintText}
        >
          <Checkbox
            checked={vm.whenDoneAssignToCreator}
            onChange={(_, data) =>
              vm.onToggleWhenDoneAssignToCreator(Boolean(data.checked))
            }
          />
        </Field>

        <Field orientation="horizontal" label={text.titleOptionsLabelText}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Checkbox
              label={text.includeProjectInTitleLabelText}
              checked={vm.includeProjectInTitle}
              onChange={(_, data) =>
                vm.onToggleIncludeProjectInTitle(Boolean(data.checked))
              }
            />
            <Checkbox
              label={text.includeTypeAcronymInTitleLabelText}
              checked={vm.includeTypeAcronymInTitle}
              onChange={(_, data) =>
                vm.onToggleIncludeTypeAcronymInTitle(Boolean(data.checked))
              }
            />
          </div>
        </Field>

        <Field
          orientation="horizontal"
          label={text.tagOptionsLabelText}
          hint={text.tagsHintText}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Checkbox
              label={text.includeParentIdAsTagLabelText}
              checked={vm.includeParentIdAsTag}
              onChange={(_, data) =>
                vm.onToggleIncludeParentIdAsTag(Boolean(data.checked))
              }
            />
            <Checkbox
              label={text.includeTagsLabelText}
              checked={vm.includeTags}
              onChange={(_, data) =>
                vm.onToggleIncludeTags(Boolean(data.checked))
              }
            />
          </div>
        </Field>
      </SettingsSectionCard>

      {(vm.isLoadingAccounts ||
        vm.isLoadingProjects ||
        vm.isLoadingTeams ||
        vm.isLoadingBoards ||
        vm.isLoadingIdentityFields ||
        vm.isLoadingStatusWorkItemTypes) && <Text>{text.loadingText}</Text>}
    </>
  );
};
